import React, {ComponentProps, useEffect, useMemo, useState} from 'react'

import keyBy from 'lodash/keyBy'

import config from '../../config'

import {
  getUpdatedDateRangeByFrom,
  getUpdatedDateRangeByTo,
  isDateRangeValid,
} from '../../../../commons/src/libs/date-range'
import {
  checkDateInputSupported,
  getDateXDaysBefore,
  getTodaysDate,
  isSameDay,
  isTodaysDate,
} from '../../../../commons/src/services/date'

import {DateRange, DateRangeConfig} from '../../../../commons/src/types'

import Button from '../../../../commons/src/components/button'
import DateInputField from '../../../../commons/src/components/date-input-field/DateInputField'
import FilterSelect from '../../../../commons/src/components/filter-select'
import FilterSelectFlyout from '../../../../commons/src/components/filter-select-flyout/filter-select-flyout'
import LabeledContent from '../../../../commons/src/components/labeled-content'
import Link from '../../../../commons/src/components/link'
import List from '../../../../commons/src/components/list'
import Paragraph from '../../../../commons/src/components/paragraph'
import RadioPillField from '../../../../commons/src/components/radio-pill-field'
import RadioPillItem from '../../../../commons/src/components/radio-pill-field/radio-pill-item'
import Stack from '../../../../commons/src/components/stack'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'

const isDateInputSupported = checkDateInputSupported()

type FilterSelectProps = ComponentProps<typeof FilterSelect>

type Props = {
  label: string
  range: DateRange
  quickAccessValues: {label: string; value: string; days: number}[]
  apply: (newRange: DateRange) => void
  onReset: () => void
  inputLabel?: string
  size?: FilterSelectProps['size']
}

const DateRangeFilterPartial = ({
  label,
  range,
  quickAccessValues,
  apply,
  onReset,
  inputLabel = 'Placement date',
  size = '100',
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dateRange, setDateRange] = useState(range)
  const [error, setError] = useState(false)

  const creationDateConfig: DateRangeConfig = config.dateRangeConfig.creationDate

  useEffect(() => {
    setDateRange(range)
  }, [range])

  const memoizedQuickAccessValue = useMemo(() => {
    const toIsToday = isTodaysDate(dateRange.to)
    if (toIsToday) {
      const filteredQuickAccessValues = quickAccessValues.filter(qa =>
        isSameDay(getDateXDaysBefore(dateRange.to, qa.days), dateRange.from)
      )
      if (filteredQuickAccessValues.length === 1) {
        return filteredQuickAccessValues[0].value
      }
    }
    return ''
  }, [dateRange])
  const quickAccessByValue = keyBy(quickAccessValues, 'value')

  const memoizedMinDate = useMemo(
    () =>
      creationDateConfig.earliestFromTodayDays
        ? getDateXDaysBefore(getTodaysDate(), creationDateConfig.earliestFromTodayDays)
        : creationDateConfig.defaultMinDate,
    [creationDateConfig]
  )

  const handleRangeChange = (newDateRange: DateRange | null) => {
    if (newDateRange) {
      setDateRange(newDateRange)
    } else {
      onReset()
      setIsOpen(false)
    }
  }

  const handleSubmit = () => {
    if (isDateRangeValid(dateRange, creationDateConfig, memoizedMinDate)) {
      apply(dateRange)
      setIsOpen(false)
    } else {
      setError(true)
    }
  }

  const handleClear = () => {
    onReset()
    setIsOpen(false)
  }

  return (
    <FilterSelect
      label={label}
      size={size}
      active
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      onShouldClose={() => setIsOpen(false)}
      menu={
        <FilterSelectFlyout
          onClear={handleClear}
          clearLabel="Reset"
          submitButton={
            <Button block onClick={handleSubmit} disabled={error}>
              Apply
            </Button>
          }
        >
          {!isDateInputSupported && (
            <Paragraph warning>
              Your current browser version does not support date pickers. Please update your browser
              and try again.
            </Paragraph>
          )}
          {isDateInputSupported && (
            <Stack space="90">
              {error && (
                <Paragraph error size="xs">
                  The selected date range is invalid. Please check our rules and requirements{' '}
                  <TooltipHandler
                    openOnHover
                    timeout={500}
                    tooltip={
                      <Tooltip
                        footer={
                          <List>
                            {creationDateConfig.earliestFromTodayDays && (
                              <li>{`You can only go back ${creationDateConfig.earliestFromTodayDays} days`}</li>
                            )}
                            <li>{`The maximum date range is ${creationDateConfig.maxDaysRange} days`}</li>
                            <li>It is not possible to select a date in the future</li>
                            <li>{`The "To" date cannot be before the "From" date`}</li>
                          </List>
                        }
                      >
                        Rules for placement date interval:
                      </Tooltip>
                    }
                  >
                    <Link> here</Link>
                  </TooltipHandler>
                  .
                </Paragraph>
              )}
              <LabeledContent title="From">
                <DateInputField
                  value={dateRange.from}
                  name="creationDateFrom"
                  onChange={newFromValue =>
                    handleRangeChange(
                      getUpdatedDateRangeByFrom(
                        creationDateConfig.maxDaysRange,
                        dateRange.to,
                        newFromValue
                      )
                    )
                  }
                  label={inputLabel}
                  min={memoizedMinDate}
                  max={getTodaysDate()}
                  error={error}
                />
              </LabeledContent>
              <LabeledContent title="To">
                <DateInputField
                  value={dateRange.to}
                  name="creationDateTo"
                  onChange={newToValue =>
                    handleRangeChange(
                      getUpdatedDateRangeByTo(
                        creationDateConfig.maxDaysRange,
                        dateRange.from,
                        newToValue
                      )
                    )
                  }
                  label={inputLabel}
                  min={memoizedMinDate}
                  max={getTodaysDate()}
                  error={error}
                />
              </LabeledContent>
              <LabeledContent title="Quick access">
                <RadioPillField
                  multiColumn
                  value={memoizedQuickAccessValue}
                  onChange={value => {
                    const days = quickAccessByValue[value].days
                    const newFrom = getDateXDaysBefore(getTodaysDate(), days)
                    setDateRange({to: getTodaysDate(), from: newFrom})
                    setError(false)
                  }}
                >
                  {quickAccessValues.map(qa => (
                    <RadioPillItem key={qa.value} value={`${qa.value}`} label={qa.label} />
                  ))}
                </RadioPillField>
              </LabeledContent>
            </Stack>
          )}
        </FilterSelectFlyout>
      }
    />
  )
}

export default DateRangeFilterPartial
